import React, { useEffect, useState } from "react"
import { Col, Form, Modal, ModalBody, ModalHeader, Row } from "reactstrap"
import * as Yup from "yup"
import { useFormik } from "formik"
import {
  DateSection,
  BasicInfoSection,
  PriceDetailsSection,
} from "pages/vendor/Offers/Modals/AddNewOffer/Sections"
import { useTranslation } from "react-i18next"

interface AddNewOfferProps {
  isOpen: boolean
  toggle: () => void
  formSubmitedHandler: () => void
}

export type CurrentStage = "addNewOffer" | "preview"

export const offerTypes: { id: string; name: string }[] = [
  "Product",
  "Category",
  "Featured",
  "Banner",
].map(offerType => ({ id: offerType, name: offerType }))

export const AddNewOffer: React.FC<AddNewOfferProps> = ({
  isOpen,
  toggle,
  formSubmitedHandler,
}) => {
  const { t } = useTranslation()

  const [openFirstAccordion, setOpenFirstAccordion] = useState("0")
  const [openSecondAccordion, setOpenSecondAccordion] = useState("0")
  const [openThirdAccordion, setOpenThirdAccordion] = useState("0")
  const [currentStage, setCurrentStage] = useState<CurrentStage>("addNewOffer")

  const [selectedofferType, setselectedofferType] = useState<{
    id: string
    name: string
  } | null>(null)

  const validation = useFormik({
    enableReinitialize: true,
    validateOnChange: true,
    validateOnBlur: true,
    validateOnMount: true,
    initialValues: {
      offerTitle: "",
      products: [] as { id: string; name: string }[],
      offerType: null as { id: string; name: string } | null,
      offerImage: null as {
        id: string
        name: string
        file?: File
        preview?: string
      } | null,
      startDate: "",
      endDate: "",
      timeFrom: "",
      timeTo: "",
      price: 0,
    },
    validationSchema: Yup.object({
      offerTitle: Yup.string()
        .trim()
        .required(t("offers_offerTitleValidationMessage") as string),
      products: Yup.array().of(
        Yup.object().shape({
          id: Yup.string().required(
            t("offers_productIdValidationMessage") as string
          ),
          name: Yup.string().required(
            t("offers_productNameValidationMessage") as string
          ),
        })
      ),
      offerType: Yup.object({
        id: Yup.string().required(
          t("offers_offerTypeValidationMessage") as string
        ),
        name: Yup.string().required(
          t("offers_offerTypeValidationMessage") as string
        ),
      }),
      //   offerImage: Yup.object(),
      startDate: Yup.date()
        .required(t("offers_startDateValidationMessage") as string)
        .max(
          Yup.ref("endDate"),
          t("offers_maxStartDateValidationMessage") as string
        ),
      endDate: Yup.date()
        .required(t("offers_endDateValidationMessage") as string)
        .min(
          Yup.ref("startDate"),
          t("offers_minEndDateValidationMessage") as string
        ),
      timeFrom: Yup.string().required(
        t("offers_startTimeValidationMessage") as string
      ),
      timeTo: Yup.string().required(
        t("offers_endTimeValidationMessage") as string
      ),
      price: Yup.number()
        .min(1, t("offers_minPriceValidationMessage") as string)
        .required(t("offers_priceValidationMessage") as string),
    }),
    onSubmit: values => {
      const {
        offerImage,
        offerTitle,
        offerType,
        endDate,
        price,
        products,
        startDate,
        timeFrom,
        timeTo,
      } = values
      console.log(values)

      // TODO: Dispatch
      validation.resetForm()
      toggle()
      formSubmitedHandler()
    },
  })

  useEffect(() => {
    validation.resetForm()
    setCurrentStage("addNewOffer")
  }, [isOpen])

  return (
    <Modal isOpen={isOpen} toggle={toggle} size="lg">
      <ModalHeader toggle={toggle} tag="h3">
        <h4 className="text-capitalize fw-semibold font-size-24">
          {currentStage === "addNewOffer"
            ? t("offers_addNewOffer")
            : t("offers_preview")}
        </h4>
      </ModalHeader>
      <ModalBody>
        <Form
          onSubmit={e => {
            if (currentStage === "addNewOffer") return
            e.preventDefault()
            validation.handleSubmit()
            return false
          }}
        >
          <BasicInfoSection
            openFirstAccordion={openFirstAccordion}
            selectedofferType={selectedofferType}
            setOpenFirstAccordion={setOpenFirstAccordion}
            setselectedofferType={setselectedofferType}
            validation={validation}
            currentStage={currentStage}
          />

          <DateSection
            openSecondAccordion={openSecondAccordion}
            setOpenSecondAccordion={setOpenSecondAccordion}
            validation={validation}
            currentStage={currentStage}
          />

          <PriceDetailsSection
            currentStage={currentStage}
            openThirdAccordion={openThirdAccordion}
            setOpenThirdAccordion={setOpenThirdAccordion}
            validation={validation}
          />

          <Row>
            <Col>
              <div className="d-flex justify-content-end gap-2 mt-4">
                {currentStage === "addNewOffer" ? (
                  <button
                    type="button"
                    className={`btn btn-primary save-customer`}
                    disabled={!(validation.isValid && validation.dirty)}
                    onClick={() =>
                      setTimeout(() => setCurrentStage("preview"), 100)
                    }
                  >
                    {t("offers_addOffer")}
                  </button>
                ) : (
                  <button
                    // type="submit"
                    className={`btn btn-primary save-customer`}
                    disabled={!(validation.isValid && validation.dirty)}
                  >
                    {t("offers_submitOffer")}
                  </button>
                )}
                <button
                  onClick={toggle}
                  type="button"
                  className="btn btn-cancel text-primary border border-primary"
                >
                  {t("common_cancel")}
                </button>
              </div>
            </Col>
          </Row>
        </Form>
      </ModalBody>
    </Modal>
  )
}
